import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Briefcase, Check, IndianRupee, MapPin, Phone, Users } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { api, apiErrorMessage, rows } from "@/lib/api";
import { useRequireAuth } from "@/lib/useRequireAuth";
import { localized } from "@/theme/tenantTheme";
import { ShareButton } from "@/components/ShareButton";
import { FavoriteButton } from "@/components/FavoriteButton";
import { Button } from "@/components/ui/button";
import { payText } from "@/components/JobCard";

/** Where a shared job opens: read-only and crawlable, applying happens in the app. */
export default function JobDetail() {
  const { id } = useParams();
  const { t, lang, auth } = useApp();
  const navigate = useNavigate();
  const requireAuth = useRequireAuth();
  const [job, setJob] = useState(null);
  const [missing, setMissing] = useState(false);
  const [applied, setApplied] = useState(false);
  const [composing, setComposing] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setMissing(false);
    setJob(null);
    api
      .get(`/city/jobs/${id}`)
      .then(({ data }) => setJob(data))
      .catch(() => setMissing(true));
  }, [id]);

  useEffect(() => {
    setApplied(false);
    if (!auth) return undefined;
    let alive = true;
    api
      .get("/job-applications/mine")
      .then((r) => alive && setApplied(rows(r.data).some((x) => x.job_id === id)))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [id, auth]);

  const apply = async () => {
    if (!requireAuth()) return;
    setBusy(true);
    try {
      await api.post(`/city/jobs/${id}/apply`, { note: note.trim() });
      setApplied(true);
      setComposing(false);
      toast.success(t("community.applySaved"));
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setBusy(false);
    }
  };

  if (missing)
    return (
      <div className="mx-auto max-w-3xl px-5 py-24 text-center" data-testid="job-not-found">
        <p className="text-lg">{t("jobs.notFound")}</p>
        <Button className="mt-6 rounded-full" onClick={() => navigate("/")} data-testid="job-back-btn">
          {t("jobs.backToList")}
        </Button>
      </div>
    );
  if (!job) return <div className="px-5 py-24 text-center text-muted-foreground">{t("common.loading")}</div>;

  const title = localized(lang, job, "title");
  const facts = [
    { icon: Briefcase, value: t(`jobs.types.${job.job_type}`) },
    { icon: IndianRupee, value: payText(job, t) },
    { icon: MapPin, value: job.area },
    { icon: Users, value: job.openings ? t("jobs.openings", { count: job.openings }) : "" },
  ].filter((fact) => fact.value);

  return (
    <div className="mx-auto max-w-4xl px-5 py-10 sm:px-8" data-testid="job-detail-page">
      <button
        type="button"
        onClick={() => navigate("/")}
        className="mb-5 inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        data-testid="job-home-link"
      >
        <ArrowLeft size={14} /> {t("jobs.backToList")}
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <h1 className="font-heading text-4xl font-bold tracking-tight sm:text-5xl" data-testid="job-title">
          {title}
        </h1>
        <div className="flex items-center gap-2">
          <FavoriteButton targetType="job" targetId={job.id} />
          <ShareButton path={`/jobs/${job.id}`} title={title} testId="job-share-btn" />
        </div>
      </div>

      {job.employer_name && (
        <p className="mt-3 text-base text-muted-foreground" data-testid="job-employer">
          {job.employer_name}
        </p>
      )}

      <div className="mt-8 flex flex-wrap gap-3" data-testid="job-facts">
        {facts.map((fact) => (
          <span
            key={fact.value}
            className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm"
          >
            <fact.icon size={14} className="text-[hsl(var(--primary))]" /> {fact.value}
          </span>
        ))}
      </div>

      <p
        className="mt-10 whitespace-pre-line text-base leading-relaxed text-muted-foreground"
        data-testid="job-description"
      >
        {localized(lang, job, "description")}
      </p>

      <div className="mt-10 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => (requireAuth() ? setComposing(true) : undefined)}
          disabled={applied || composing}
          className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5 disabled:opacity-80 disabled:hover:translate-y-0"
          data-testid="job-apply-btn"
        >
          {applied ? (
            <>
              <Check size={15} /> {t("community.applied")}
            </>
          ) : (
            t("community.apply")
          )}
        </button>
        {job.contact_phone && (
          <a
            href={`tel:${job.contact_phone}`}
            className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold transition-transform hover:-translate-y-0.5"
            data-testid="job-call-btn"
          >
            <Phone size={15} /> {job.contact_phone}
          </a>
        )}
      </div>

      {composing && !applied && (
        <div className="mt-5 max-w-xl space-y-3" data-testid="job-apply-form">
          <textarea
            className="w-full rounded-2xl border border-border bg-background p-3 text-sm"
            rows={3}
            maxLength={500}
            placeholder={t("community.applyNote")}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid="job-apply-note"
          />
          <Button className="rounded-full" onClick={apply} disabled={busy} data-testid="job-apply-send">
            {t("community.applySend")}
          </Button>
        </div>
      )}
    </div>
  );
}
